import { createFileRoute } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";

import { loadClientRecord } from "@/lib/client-api";
import { ClientRecordShell } from "@/components/client-record-shell";
import {
  ClientPdfShell,
  ClientNotAuthenticated,
  ClientNotAvailable,
} from "@/components/client-pdf-shell";

/*
 * The portal route for a shared record that has no per-entity route of
 * its own. `pages`, `content_assets` and `invoices` each carry a
 * dedicated route; every other aggregate a tenant flags
 * `sharable_via_portal` lands here, so flagging one is enough to make
 * it readable without shipping a route alongside it.
 *
 * Not nested under a portal kind. The record is identified by its
 * aggregate and id, which are the same facts whichever portal the
 * visitor signed in to — matching the `/content/{id}` and
 * `/invoice/{id}` routes it sits beside.
 */

export const Route = createFileRoute("/$tenantSlug/record/$entitySlug/$recordId")({
  component: SharedRecordView,
});

function SharedRecordView() {
  const { tenantSlug, entitySlug, recordId } = Route.useParams();
  const { data, isLoading, isError, error } = useQuery({
    queryKey: ["portal-record", tenantSlug, entitySlug, recordId] as const,
    queryFn: () => loadClientRecord(tenantSlug, entitySlug, recordId),
    retry: false,
  });

  if (isLoading) {
    return (
      <div className="flex-1 grid place-items-center text-sm text-slate-500">
        Loading&hellip;
      </div>
    );
  }

  if (isError) {
    const err = error as (Error & { status?: number }) | undefined;
    if (err?.status === 401) {
      return <ClientNotAuthenticated tenantSlug={tenantSlug} />;
    }
    return <ClientNotAvailable tenantSlug={tenantSlug} message={err?.message} />;
  }

  if (!data) {
    return <ClientNotAvailable tenantSlug={tenantSlug} />;
  }

  // Invoices and statements reach this route only when opened by id
  // rather than through their own route; the PDF shell carries the
  // download action their envelope expects.
  if (data.view_type === "pdf") {
    return (
      <ClientPdfShell
        tenantSlug={tenantSlug}
        view={data}
        fallbackTitle={`Record #${recordId}`}
      />
    );
  }

  return (
    <ClientRecordShell
      tenantSlug={tenantSlug}
      view={data}
      fallbackTitle={`Record #${recordId}`}
    />
  );
}
